import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Save, MessageSquare, Mail } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLang } from "@/context/LanguageContext";
import api, { formatApiError } from "@/lib/api";

export default function AdminSettings() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ twilio_account_sid: "", twilio_auth_token: "", twilio_from_number: "", twilio_verify_service: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v));
      const { data } = await api.put("/admin/settings", payload);
      setS(data);
      setForm({ twilio_account_sid: "", twilio_auth_token: "", twilio_from_number: "", twilio_verify_service: "" });
      toast.success("Settings saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  if (!s) return (<div className="min-h-screen bg-[#FAF6F0]"><Navbar /><div className="p-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div></div>);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF6F0]">
      <Navbar />
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#8B1E2B]" data-testid="back-to-queue"><ArrowLeft className="h-4 w-4" /> {t("admin_queue")}</button>
        <h1 className="mt-4 font-serif text-3xl text-[#8B1E2B]" data-testid="settings-title">{t("settings")}</h1>

        <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-sm text-emerald-900 flex gap-3">
          <Mail className="h-5 w-5 shrink-0" />
          <div>
            <strong>Email notifications are active</strong> via the platform-managed email service. Applicants automatically receive updates on submission and status changes.
          </div>
        </div>

        <div className="mt-6 rounded-2xl bg-white border border-amber-500/20 p-6 shadow-sm" data-testid="twilio-settings">
          <h3 className="font-serif text-xl text-slate-900 flex items-center gap-2"><MessageSquare className="h-5 w-5 text-[#8B1E2B]" /> SMS notifications (Twilio)</h3>
          <p className="mt-1 text-sm text-slate-500">
            Optional. Provide your Twilio credentials to also send SMS updates. Status: {" "}
            <span className={s.twilio_configured ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"} data-testid="twilio-status">
              {s.twilio_configured ? `Configured (${s.twilio_account_sid})` : "Not configured — YOU MUST SUPPLY THESE KEYS"}
            </span>
          </p>

          <div className="mt-5 space-y-4">
            <div>
              <Label htmlFor="sid">Twilio Account SID</Label>
              <Input id="sid" value={form.twilio_account_sid} onChange={(e) => setForm((f) => ({ ...f, twilio_account_sid: e.target.value }))} className="mt-1.5" placeholder="ACxxxxxxxx" data-testid="twilio-sid-input" />
            </div>
            <div>
              <Label htmlFor="token">Twilio Auth Token</Label>
              <Input id="token" type="password" value={form.twilio_auth_token} onChange={(e) => setForm((f) => ({ ...f, twilio_auth_token: e.target.value }))} className="mt-1.5" placeholder="••••••••" data-testid="twilio-token-input" />
            </div>
            <div>
              <Label htmlFor="from">Twilio From Number (for SMS updates)</Label>
              <Input id="from" value={form.twilio_from_number} onChange={(e) => setForm((f) => ({ ...f, twilio_from_number: e.target.value }))} className="mt-1.5" placeholder="+1..." defaultValue={s.twilio_from_number} data-testid="twilio-from-input" />
            </div>
            <div>
              <Label htmlFor="verify">Twilio Verify Service SID (for mobile OTP login)</Label>
              <Input id="verify" value={form.twilio_verify_service} onChange={(e) => setForm((f) => ({ ...f, twilio_verify_service: e.target.value }))} className="mt-1.5" placeholder="VAxxxxxxxx" data-testid="twilio-verify-input" />
              <p className="mt-1 text-xs text-slate-500">Mobile OTP sign-in status: {" "}
                <span className={s.otp_enabled ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"} data-testid="otp-status">
                  {s.otp_enabled ? "Enabled" : "Disabled — add a Verify Service SID to enable"}
                </span>
              </p>
            </div>
            <Button onClick={save} disabled={saving} className="bg-[#8B1E2B] hover:bg-[#6f1622] text-white" data-testid="settings-save-button">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} {t("save")}
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
